import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";

import { SOUND_NAME } from "../../../sounds";
import anime from "animejs/lib/anime.es.js";
import PotCollectionGrandJackpotDialogController from "./PotCollectionGrandJackpotDialogController";
import { safeInit, attemptFullscreenToggle } from "../../../utilities";
import { selectShowPotCollectionGrandJackpot } from "../../../State/Slices/animationsSlice";
import store from "../../../State/Store/store";
import { soundManager } from "../../../SoundManager";

// Overlay settings (matching WinAnimationSecondary / PotCollectionOutroDialog)


/**
 * PotCollectionGrandJackpotDialog
 *
 * Grand Jackpot congratulations popup for the Pot Collection bonus game.
 * Shown when the grid is completely filled (15/15 coins) — GDD §14.1–14.2.
 *
 * Uses the win_celebration spine with the "grand_jackpot" skin.
 * Identical structure to PotCollectionOutroDialog but:
 *   - Uses grand_jackpot skin instead of total_win
 *   - Total ~3s auto-close synced to gjp_win sound (not 30s)
 *   - Does NOT trigger scene transition on dismiss
 *   - Shows congratulations + you won text
 *   - GJP multiplier shown as "5,000x" from state
 */
const PotCollectionGrandJackpotDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showGrandJackpot = useSelector(selectShowPotCollectionGrandJackpot);

  const gjpControllerRef = useRef(null);
  const gjpContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const handleUserDismissRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;

  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: POPUP_OVERLAY_CONFIG.ALPHA,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: POPUP_OVERLAY_CONFIG.ALPHA },
        alpha: 0,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once
  useEffect(() => {
    if (gjpContainerRef.current && !gjpControllerRef.current) {
      safeInit(() => {
        gjpControllerRef.current = new PotCollectionGrandJackpotDialogController(
          gjpContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
        );
        // "TAP TO CONTINUE" text routes through the same dismiss flow (fullscreen
        // + sound + close) as the full-screen overlay. Proxy via ref so it always
        // calls the latest handleUserDismiss, not a stale mount-time closure.
        gjpControllerRef.current.setButtonClickHandler((...args) => {
          if (handleUserDismissRef.current) handleUserDismissRef.current(...args);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (gjpControllerRef.current) {
      gjpControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Trigger the sequence when showGrandJackpot becomes true
  useEffect(() => {
    if (gjpControllerRef.current && showGrandJackpot) {
      // GDD §14.1: Show the raw multiplier (e.g. "5,000x") from state
      const gjpMultiplier = store.getState().game?.jackpotMultipliers?.grand || 5000;
      gjpControllerRef.current.setGrandJackpotMultiplier(gjpMultiplier);
      gjpControllerRef.current.startSequence();
    }
  }, [showGrandJackpot]);

  // Handle user click: fullscreen (if needed) + dismiss
  const handleUserDismiss = useCallback(
    async (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      await attemptFullscreenToggle(dispatch, device);

      if (gjpControllerRef.current && showGrandJackpot) {
        const accepted = gjpControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showGrandJackpot, dispatch, device],
  );

  // Keep ref current so the controller's proxy always calls the latest version
  handleUserDismissRef.current = handleUserDismiss;

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showGrandJackpot || !gjpControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        await handleUserDismiss(event);
      } catch {
        if (
          gjpControllerRef.current &&
          gjpControllerRef.current.finishAnimation
        ) {
          gjpControllerRef.current.finishAnimation();
        }
      }
    };

    if (showGrandJackpot) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showGrandJackpot, handleUserDismiss]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gjpControllerRef.current) {
        gjpControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(POPUP_OVERLAY_CONFIG.COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="pot_collection_grand_jackpot"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={10}
      eventMode={showGrandJackpot ? "auto" : "none"}
      ref={gjpContainerRef}
    >
      {/* Background overlay */}
      <Graphics
        name="pot_collection_gjp_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showGrandJackpot ? "static" : "none"}
        pointerdown={handleUserDismiss}
        cursor="default"
      />

      {/* Spine animation, text, and button managed by PotCollectionGrandJackpotDialogController */}
    </Container>
  );
};

export default memo(PotCollectionGrandJackpotDialog);
